import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useFormik } from 'formik';

import { ReactComponent as EmailIcon } from '../../assets/icons/contacts/email.svg';
import { ReactComponent as InfoIcon } from '../../assets/icons/contacts/info.svg';
import { ReactComponent as PhoneIcon } from '../../assets/icons/contacts/phone.svg';
import { ReactComponent as SendIcon } from '../../assets/icons/contacts/send.svg';
import AppButton from '../../components/designe/buttons/app-button/AppButton';
import AppFormTextField from '../../components/designe/forms/app-form-text-field';
import PageContainer from '../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../components/layout/page-container/page-container-error-handler';
import { successToastMessage } from '../../components/layout/toast-root/toast';
import { useGetAppVariables } from '../../redux/api/appConfigApi';
import { usePostSupportContact } from '../../redux/api/supportApi';
import {
  AppVariableKeys,
  getAppVariableValueInsensitive,
} from '../../types/interfaces/app-variable';
import { useStyles } from './Contacts.styles';
import {
  CONTACT_FORM_INITIAL_VALUES,
  getContactFormValidation,
} from './FormikData';

function sanitizePhoneForTelHref(displayPhone: string): string {
  const trimmed = displayPhone.trim();
  const international = trimmed.startsWith('+');
  const digitsOnly = displayPhone.replace(/\D/g, '');
  if (international && digitsOnly.length > 0) {
    return `+${digitsOnly}`;
  }
  return digitsOnly;
}

function Contacts() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const [postSupportContact, { isLoading: isContactFormSubmitting }] =
    usePostSupportContact();

  const {
    data: variables,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetAppVariables();

  const email = getAppVariableValueInsensitive(
    variables,
    AppVariableKeys.contactUsEmail,
  );
  const phone = getAppVariableValueInsensitive(
    variables,
    AppVariableKeys.contactUsPhone,
  );

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const formik = useFormik({
    initialValues: CONTACT_FORM_INITIAL_VALUES,
    validationSchema: getContactFormValidation(t),
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, helpers) => {
      await postSupportContact({
        body: {
          name: values.name.trim(),
          email: values.email.trim(),
          title: values.subject.trim(),
          message: values.message.trim(),
        },
      }).unwrap();
      helpers.resetForm();
      successToastMessage(t('contacts.formSuccess'));
    },
  });

  useRegisterPageContainerError({
    errors: isError && error ? [error] : [],
    isLoading: !variables && (isLoading || isFetching),
    hasData: Boolean(variables?.length),
    onRetry: handleRetry,
  });

  return (
    <PageContainer title={t('contacts.title')} onRefresh={() => void refetch()}>
      <Box className={classes.contactsWrapper}>
        <Box className={classes.infoCard}>
          <Box className={classes.introRow}>
            <InfoIcon className={classes.formTitleIcon} />
            <Typography variant="h2">{t('contacts.intro')}</Typography>
          </Box>
        </Box>
        <Box className={classes.contactRowCard}>
          <Box className={classes.contactRow}>
            <Box className={classes.rowIcon}>
              <EmailIcon />
            </Box>
            <Box className={classes.contactContent}>
              <Typography variant="text-sm" color="text.secondary">
                {t('contacts.emailLabel')}
              </Typography>
              {email ? (
                <Link
                  href={`mailto:${email}`}
                  color="inherit"
                  underline="hover">
                  <Typography variant="text-md" component="span">
                    {email}
                  </Typography>
                </Link>
              ) : (
                <Typography variant="text-md" color="text.secondary">
                  —
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box className={classes.contactRowCard}>
          <Box className={classes.contactRow}>
            <Box className={classes.rowIcon}>
              <PhoneIcon />
            </Box>
            <Box className={classes.contactContent}>
              <Typography variant="text-sm" color="text.secondary">
                {t('contacts.phoneLabel')}
              </Typography>
              {phone ? (
                <Link
                  href={`tel:${sanitizePhoneForTelHref(phone)}`}
                  color="inherit"
                  underline="hover">
                  <Typography variant="text-md" component="span">
                    {phone}
                  </Typography>
                </Link>
              ) : (
                <Typography variant="text-md" color="text.secondary">
                  —
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box className={classes.formCard}>
          <Box className={classes.formTitleRow}>
            <SendIcon className={classes.formTitleIcon} />
            <Typography variant="text-lg">{t('contacts.formTitle')}</Typography>
          </Box>
          <form onSubmit={formik.handleSubmit} noValidate>
            <Box className={classes.formFields}>
              <AppFormTextField
                formik={formik}
                name="name"
                label={t('contacts.nameLabel')}
                placeholder={t('contacts.namePlaceholder')}
                fullWidth
              />
              <AppFormTextField
                formik={formik}
                name="email"
                label={t('contacts.emailLabel')}
                placeholder={t('contacts.emailPlaceholder')}
                fullWidth
              />
              <AppFormTextField
                formik={formik}
                name="subject"
                label={t('contacts.subjectLabel')}
                placeholder={t('contacts.subjectPlaceholder')}
                fullWidth
              />
              <AppFormTextField
                formik={formik}
                name="message"
                label={t('contacts.messageLabel')}
                placeholder={t('contacts.messagePlaceholder')}
                fullWidth
                multiline
                minRows={5}
              />
              <AppButton
                className={classes.sendButton}
                variant="contained"
                color="primary"
                type="submit"
                loading={isContactFormSubmitting}
                disabled={
                  !formik.isValid ||
                  !formik.dirty ||
                  formik.isSubmitting ||
                  isContactFormSubmitting
                }
                startIcon={isContactFormSubmitting ? undefined : <SendIcon />}>
                {t('contacts.sendEmail')}
              </AppButton>
            </Box>
          </form>
        </Box>
      </Box>
    </PageContainer>
  );
}

export default Contacts;
